/**
 * Calendrier mensuel et frise horaire (UniCalendarPanel v1) — 7.1 / 8.1.
 * Couleurs v1 : planning = sarcelle ; prestations : vert confirmé, orange
 * modifié (non conforme), gris plage grise, rouge férié.
 */
import { feriesLegaux } from '@playtime/engine';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Evenement } from '../api';
import { NOMS_JOURS, TYPES, heure, hhmm, jourDe, jourSemaine, joursDuMois, minutesDe, minutesEvenement } from '../commun';

export type Fenetre = '5h-5h' | '7h-20h' | '5h-22h';
export const FENETRES: Record<Fenetre, [number, number]> = { '5h-5h': [5, 29], '7h-20h': [7, 20], '5h-22h': [5, 22] };
const NOMS_FERIES: Record<string, string> = {
  '01-01': 'Nouvel An', '05-01': 'Fête du Travail', '07-21': 'Fête nationale', '08-15': 'Assomption', '11-01': 'Toussaint', '11-11': 'Armistice', '12-25': 'Noël',
};

/** Pâques (Meeus) — pour nommer les fériés mobiles. */
function paques(an: number): Date {
  const a = an % 19, b = Math.floor(an / 100), c = an % 100, d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mois = Math.floor((h + l - 7 * m + 114) / 31), jour = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(an, mois - 1, jour));
}

export function useFeries(annee: number, flottants: { annee: number; mois: number; jour: number; description: string | null }[]): Map<string, string> {
  return useMemo(() => {
    const m = new Map<string, string>();
    const p = paques(annee).getTime();
    const mobiles: Record<number, string> = { 1: 'Lundi de Pâques', 39: 'Ascension', 50: 'Pentecôte' };
    for (const f of feriesLegaux(annee)) {
      const iso = `${f.an}-${String(f.mois).padStart(2, '0')}-${String(f.jour).padStart(2, '0')}`;
      const delta = Math.round((Date.UTC(f.an, f.mois - 1, f.jour) - p) / 86_400_000);
      m.set(iso, NOMS_FERIES[iso.slice(5)] ?? mobiles[delta] ?? 'Férié');
    }
    for (const f of flottants) m.set(`${f.annee}-${String(f.mois).padStart(2, '0')}-${String(f.jour).padStart(2, '0')}`, f.description ?? 'Férié');
    return m;
  }, [annee, flottants]);
}

function classeEvenement(e: Evenement, prestations: boolean): string {
  if (!prestations) return 'ev planning';
  if (e.statutPrestation === 0) return 'ev grise';
  if ((e.statutPrestation ?? 0) < 0) return 'ev neutralisee';
  return e.conformeAuPlanning ? 'ev confirmee' : 'ev modifiee';
}

/** Libellé de bloc v1 : « Congé - confirmé » sur les prestations confirmées. */
function libelleBloc(e: Evenement, prestations: boolean): string {
  const base = e.typeEvenement === 1 ? (e.description || 'Travail') : TYPES[e.typeEvenement] ?? '';
  if (prestations && (e.statutPrestation ?? 0) > 0 && e.conformeAuPlanning) return `${base} - confirmé`;
  return base;
}

export function Calendrier({ mois, evenements, prestations, feries, jourSelectionne, surJour, surDoubleClic, surEvenement }: {
  mois: number; evenements: Evenement[]; prestations: boolean; feries: Map<string, string>;
  jourSelectionne: string | null; surJour: (iso: string) => void; surDoubleClic: (iso: string) => void; surEvenement: (e: Evenement) => void;
}): JSX.Element {
  const jours = joursDuMois(mois);
  const premier = jours[0]!;
  const decalage = jourSemaine(premier);
  const an = Math.trunc(mois / 100); const m = mois % 100;
  // Grille 6 semaines complètes, jours hors mois grisés (v1).
  const debutGrille = new Date(Date.UTC(an, m - 1, 1 - decalage));
  const cellules = Array.from({ length: 42 }, (_, i) => { const d = new Date(debutGrille); d.setUTCDate(debutGrille.getUTCDate() + i); return d.toISOString().slice(0, 10); });
  const parJour = useMemo(() => {
    const map = new Map<string, Evenement[]>();
    for (const e of evenements) { const j = jourDe(e.debut); (map.get(j) ?? map.set(j, []).get(j)!).push(e); }
    for (const l of map.values()) l.sort((a, b) => a.debut.localeCompare(b.debut));
    return map;
  }, [evenements]);

  return (
    <div className="cal-mois" role="grid" aria-label="Calendrier du mois">
      {NOMS_JOURS.map((n) => <div key={n} className="cal-entete">{n}</div>)}
      {cellules.map((j) => {
        const horsMois = j.slice(0, 7) !== premier.slice(0, 7);
        const evts = parJour.get(j) ?? [];
        const ferie = feries.get(j);
        return (
          <div key={j} role="gridcell" tabIndex={0} className={`cal-jour${horsMois ? ' hors-mois' : ''}${j === jourSelectionne ? ' selectionne' : ''}`} onClick={() => surJour(j)} onDoubleClick={() => surDoubleClic(j)} onKeyDown={(e) => { if (e.key === 'Enter') surDoubleClic(j); }}>
            <div className="cal-numero">{Number(j.slice(8))}</div>
            {ferie && <span className={`cal-ferie${prestations ? ' rouge' : ''}`}>{ferie}</span>}
            {evts.slice(0, 2).map((e) => (
              <button key={e.id} className={classeEvenement(e, prestations)} onClick={(ev) => { ev.stopPropagation(); surEvenement(e); }} title={`${heure(e.debut)} - ${heure(e.fin)} ${libelleBloc(e, prestations)}`}>
                {heure(e.debut)} - {heure(e.fin)}…
              </button>
            ))}
            {evts.length > 2 && <span className="cal-plus" onClick={(ev) => { ev.stopPropagation(); surJour(j); }}>+{evts.length - 2} en plus</span>}
          </div>
        );
      })}
    </div>
  );
}

/** Frise Jour / Semaine avec fenêtre horaire (5h-5h = journée pivotée à 5 h, comme la v1). */
export function Frise({ vue, jour, fenetre, evenements, prestations, feries, surEvenement, surJour, surDoubleClic, surVue, surFenetre, surDeplacement }: {
  vue: 'JOUR' | 'SEMAINE'; jour: string; fenetre: Fenetre; evenements: Evenement[]; prestations: boolean; feries: Map<string, string>;
  surEvenement: (e: Evenement) => void; surJour: (iso: string) => void; surDoubleClic: (iso: string) => void; surVue: (v: 'JOUR' | 'SEMAINE') => void; surFenetre: (f: Fenetre) => void;
  /** Glisser-déposer (v1 `EventMove` / `EventResize`) : nouveau début et nouvelle fin (heure murale ISO) après un déplacement ou un étirement. */
  surDeplacement?: (e: Evenement, debut: string, fin: string) => void;
}): JSX.Element {
  const [hDebut, hFin] = FENETRES[fenetre];
  const lundi = new Date(`${jour}T00:00:00Z`); lundi.setUTCDate(lundi.getUTCDate() - jourSemaine(jour));
  const joursVue = vue === 'JOUR' ? [jour] : Array.from({ length: 7 }, (_, i) => { const d = new Date(lundi); d.setUTCDate(lundi.getUTCDate() + i); return d.toISOString().slice(0, 10); });
  const total = hFin - hDebut;
  // Lot 4 : la frise remplit la hauteur disponible — hauteur d'heure = place / nombre d'heures, plancher 22 px
  // (au-delà, défilement interne à la frise, jamais de la page).
  const corpsRef = useRef<HTMLDivElement>(null);
  const [hauteurDispo, setHauteurDispo] = useState(0);
  useEffect(() => {
    const el = corpsRef.current; if (!el) return;
    const ro = new ResizeObserver((entries) => { for (const e of entries) setHauteurDispo(e.contentRect.height); });
    ro.observe(el); return () => ro.disconnect();
  }, []);
  const enteteSemaine = vue === 'SEMAINE' ? 44 : 0;
  const hauteurHeure = Math.max(22, Math.floor((hauteurDispo - enteteSemaine - 8) / Math.max(1, total)) || 44);

  // ── Glisser-déposer (v1 : EventMove / EventResize de la frise) ─────────────────────────────────────────────
  // Prendre un bloc et le lâcher ailleurs le déplace (autre heure, autre jour en vue Semaine) ; tirer sa poignée
  // basse l'étire. Pas de 5 minutes. Le geste ne fait que proposer de nouvelles bornes : les règles (contrat main
  // libre, mode, droits, statut, conformité) sont appliquées par l'écran appelant et par l'API. Au doigt, le
  // glissement démarre après un appui de 300 ms (sinon le doigt fait défiler la frise). Échap annule.
  type Glisse = { e: Evenement; mode: 'deplacer' | 'etirer'; x0: number; y0: number; deltaMin: number; deltaJours: number; actif: boolean; pointerId: number };
  const [glisse, setGlisse] = useState<Glisse | null>(null);
  const glisseRef = useRef<Glisse | null>(null);
  const clicASupprimer = useRef(false);
  const attenteTactile = useRef<number | null>(null);
  const majGlisse = (g: Glisse | null) => { glisseRef.current = g; setGlisse(g); };
  const pas = 5;
  const bornesApres = (g: Glisse): { debut: Date; fin: Date } => {
    const d = new Date(`${g.e.debut}Z`); const f = new Date(`${g.e.fin}Z`);
    if (g.mode === 'deplacer') { d.setUTCMinutes(d.getUTCMinutes() + g.deltaMin); f.setUTCMinutes(f.getUTCMinutes() + g.deltaMin); d.setUTCDate(d.getUTCDate() + g.deltaJours); f.setUTCDate(f.getUTCDate() + g.deltaJours); }
    else { f.setUTCMinutes(f.getUTCMinutes() + g.deltaMin); if (f.getTime() - d.getTime() < pas * 60_000) f.setTime(d.getTime() + pas * 60_000); }
    return { debut: d, fin: f };
  };
  const mural = (d: Date) => d.toISOString().slice(0, 19);
  function demarrer(e: Evenement, mode: 'deplacer' | 'etirer', ev: React.PointerEvent): void {
    if (!surDeplacement || ev.button !== 0) return;
    const g: Glisse = { e, mode, x0: ev.clientX, y0: ev.clientY, deltaMin: 0, deltaJours: 0, actif: false, pointerId: ev.pointerId };
    if (ev.pointerType === 'touch') {
      attenteTactile.current = window.setTimeout(() => { majGlisse({ ...g, actif: true }); }, 300);
      majGlisse(g);
    } else majGlisse(g);
  }
  useEffect(() => {
    if (!glisse) return;
    const largeurColonne = () => { const el = corpsRef.current; if (!el) return 0; return (el.clientWidth - 56) / Math.max(1, joursVue.length); };
    const surMouvement = (ev: PointerEvent) => {
      const g = glisseRef.current; if (!g || ev.pointerId !== g.pointerId) return;
      const dy = ev.clientY - g.y0; const dx = ev.clientX - g.x0;
      if (!g.actif) {
        if (attenteTactile.current !== null) { if (Math.abs(dx) + Math.abs(dy) > 8) { window.clearTimeout(attenteTactile.current); attenteTactile.current = null; majGlisse(null); } return; }
        if (Math.abs(dx) + Math.abs(dy) < 4) return;
        clicASupprimer.current = true;
      }
      ev.preventDefault();
      const deltaMin = Math.round((dy / hauteurHeure) * 60 / pas) * pas;
      const deltaJours = g.mode === 'deplacer' && vue === 'SEMAINE' ? Math.max(-6, Math.min(6, Math.round(dx / Math.max(1, largeurColonne())))) : 0;
      majGlisse({ ...g, actif: true, deltaMin, deltaJours });
    };
    const surFin = (ev: PointerEvent) => {
      const g = glisseRef.current; if (!g || ev.pointerId !== g.pointerId) return;
      if (attenteTactile.current !== null) { window.clearTimeout(attenteTactile.current); attenteTactile.current = null; }
      majGlisse(null);
      if (!g.actif || (g.deltaMin === 0 && g.deltaJours === 0)) return;
      const { debut, fin } = bornesApres(g);
      surDeplacement?.(g.e, mural(debut), mural(fin));
    };
    const surTouche = (ev: KeyboardEvent) => { if (ev.key === 'Escape') { if (attenteTactile.current !== null) window.clearTimeout(attenteTactile.current); attenteTactile.current = null; majGlisse(null); } };
    window.addEventListener('pointermove', surMouvement, { passive: false });
    window.addEventListener('pointerup', surFin); window.addEventListener('pointercancel', surFin); window.addEventListener('keydown', surTouche);
    return () => { window.removeEventListener('pointermove', surMouvement); window.removeEventListener('pointerup', surFin); window.removeEventListener('pointercancel', surFin); window.removeEventListener('keydown', surTouche); };
  }, [glisse !== null, hauteurHeure, vue, joursVue.length]);
  const heures = Array.from({ length: total + 1 }, (_, i) => (hDebut + i) % 24);
  const evtsDe = (j: string) => evenements.filter((e) => jourDe(e.debut) === j && (!prestations || (e.statutPrestation ?? 0) >= 0));
  const minutesJour = (j: string) => evtsDe(j).filter((e) => e.typeEvenement === 1).reduce((a, e) => a + minutesEvenement(e), 0);
  const semaine = Array.from({ length: 7 }, (_, i) => { const d = new Date(lundi); d.setUTCDate(lundi.getUTCDate() + i); return d.toISOString().slice(0, 10); });
  const minutesSemaine = semaine.reduce((a, j) => a + minutesJour(j), 0);
  const jourLibelle = (j: string) => `${['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'][jourSemaine(j)]} ${j.slice(8)}`;
  const titre = vue === 'JOUR'
    ? `${['Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.', 'Dim.'][jourSemaine(jour)]} ${jour.split('-').reverse().join('/')}`
    : `${semaine[0]!.slice(8)}/${semaine[0]!.slice(5, 7)} - ${semaine[6]!.slice(8)}/${semaine[6]!.slice(5, 7)}/${semaine[6]!.slice(0, 4)}`;
  const decaler = (delta: number) => { const d = new Date(`${jour}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + delta * (vue === 'JOUR' ? 1 : 7)); surJour(d.toISOString().slice(0, 10)); };

  return (
    <div className="frise">
      <div className="frise-entete">
        <span className="navigation-mois"><button aria-label="Précédent" onClick={() => decaler(-1)}>&lt;</button><button aria-label="Suivant" onClick={() => decaler(1)}>&gt;</button></span>
        <span className="frise-titre">{titre}</span>
        <span className="fenetres">{(Object.keys(FENETRES) as Fenetre[]).map((f) => <label key={f} className="case"><input type="radio" name="fenetre" checked={fenetre === f} onChange={() => surFenetre(f)} /> {f}</label>)}</span>
        <span className="onglets petit"><button aria-selected={vue === 'JOUR'} onClick={() => surVue('JOUR')}>Jour</button><button aria-selected={vue === 'SEMAINE'} onClick={() => surVue('SEMAINE')}>Semaine</button></span>
      </div>
      <div className="frise-corps" ref={corpsRef} style={{ gridTemplateColumns: `56px repeat(${joursVue.length}, 1fr)` }}>
        {vue === 'SEMAINE' && <><div /> {joursVue.map((j) => <div key={j} className="frise-jour-entete">{jourLibelle(j)}{feries.get(j) && <span className={`cal-ferie${prestations ? ' rouge' : ''}`}>{feries.get(j)}</span>}</div>)}</>}
        <div className="frise-heures" style={{ height: total * hauteurHeure }}>
          {heures.map((h, i) => <div key={i} style={{ top: i * hauteurHeure }}>{String(h).padStart(2, '0')}:00</div>)}
        </div>
        {joursVue.map((j) => (
          <div key={j} className="frise-colonne" style={{ height: total * hauteurHeure }} onClick={() => surJour(j)} onDoubleClick={() => surDoubleClic(j)}>
            {heures.map((_, i) => <div key={i} className="frise-ligne" style={{ top: i * hauteurHeure }} />)}
            {evtsDe(j).map((e) => {
              const enCours = glisse?.actif && glisse.e.id === e.id ? glisse : null;
              const bornes = enCours ? bornesApres(enCours) : null;
              const debutAff = bornes ? mural(bornes.debut) : e.debut; const finAff = bornes ? mural(bornes.fin) : e.fin;
              let d = minutesDe(heure(debutAff)); let f = minutesDe(heure(finAff));
              if (jourDe(finAff) !== jourDe(debutAff)) f = 24 * 60 + minutesDe(heure(finAff));
              const debutFen = hDebut * 60; const finFen = hFin * 60;
              const dd = Math.max(d, debutFen); const ff = Math.min(f, finFen);
              if (ff <= dd) return null;
              const top = ((dd - debutFen) / 60) * hauteurHeure; const hauteur = ((ff - dd) / 60) * hauteurHeure;
              const decalageColonnes = enCours ? enCours.deltaJours : 0;
              return (
                <button key={e.id} className={`${classeEvenement(e, prestations)} bloc${enCours ? ' en-glissement' : ''}${surDeplacement ? ' deplacable' : ''}`}
                  style={{ top, height: Math.max(hauteur, 18), transform: decalageColonnes ? `translateX(calc(${decalageColonnes} * (100% + 8px)))` : undefined }}
                  onPointerDown={(ev) => demarrer(e, 'deplacer', ev)}
                  onClick={(ev) => { ev.stopPropagation(); if (clicASupprimer.current) { clicASupprimer.current = false; return; } surEvenement(e); }}>
                  <span className="heure">{heure(debutAff)} - {heure(finAff)}</span><br />{libelleBloc(e, prestations)}
                  {surDeplacement && <span className="poignee-etirement" onPointerDown={(ev) => { ev.stopPropagation(); demarrer(e, 'etirer', ev); }} />}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="frise-totaux">
        <span>{prestations ? 'Heures encodées ce' : 'Heures planifiées ce'} {jour.split('-').reverse().join('/')} : <strong>{hhmmV1(minutesJour(jour))}</strong></span>
        <span>{prestations ? 'Heures encodées sur la semaine' : 'Heures planifiées sur la semaine'} : <strong>{hhmmV1(minutesSemaine)}</strong></span>
      </div>
    </div>
  );
}

/** Format v1 « 7 h 30 ». */
export function hhmmV1(minutes: number): string {
  const s = hhmm(minutes);
  const [h, m] = s.split(':');
  return `${Number(h)} h ${m}`;
}
